/**
 * The motion tokens, for the animations a script runs. Request of 5 October
 * 2026, section 1. The same values as src/ui/motion.css, which every
 * stylesheet uses; docs/COMPONENT-REGISTRY.md has both.
 *
 * One approach across the app: CSS transitions and keyframes for what a
 * stylesheet can say, and the browser's own Web Animations API for the few
 * things it cannot (a list arriving in sequence, an item moving to its new
 * place, a field shaking). No animation library: the platform has everything
 * needed, and a technician's phone downloads nothing more for it.
 *
 * Every helper only moves `transform` and `opacity`, never waits to let data
 * or a tap through, and does nothing where the browser cannot animate, which
 * includes the test environment.
 */

export const DURATION = {
  instant: 100,
  fast: 180,
  normal: 280,
  slow: 400,
} as const;

/** Leaving takes about three quarters of arriving. */
export const EXIT = {
  fast: 140,
  normal: 200,
} as const;

export const EASE = {
  enter: 'cubic-bezier(0.2, 0, 0, 1)',
  exit: 'cubic-bezier(0.3, 0, 1, 1)',
  move: 'cubic-bezier(0.4, 0, 0.2, 1)',
  spring: 'cubic-bezier(0.32, 0.72, 0, 1)',
} as const;

export const SHIFT = { sm: 8, md: 16 } as const;

/** Between items of a list arriving in sequence, and how many are staggered at most. */
export const STAGGER_MS = 30;
export const STAGGER_MAX = 8;

/** Whether the person asked their device for less motion. */
export function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function canAnimate(element: Element | null | undefined): element is HTMLElement {
  return element instanceof HTMLElement && typeof element.animate === 'function';
}

export type Entrance = 'fade' | 'rise' | 'fromRight' | 'fromLeft' | 'pop';

function frames(kind: Entrance): Keyframe[] {
  if (reducedMotion() || kind === 'fade') return [{ opacity: 0 }, { opacity: 1 }];
  switch (kind) {
    case 'rise':
      return [
        { opacity: 0, transform: `translateY(${String(SHIFT.sm)}px)` },
        { opacity: 1, transform: 'none' },
      ];
    case 'fromRight':
      return [
        { opacity: 0, transform: `translateX(${String(SHIFT.md * 2)}px)` },
        { opacity: 1, transform: 'none' },
      ];
    case 'fromLeft':
      return [
        { opacity: 0, transform: `translateX(-${String(SHIFT.md * 2)}px)` },
        { opacity: 1, transform: 'none' },
      ];
    case 'pop':
      return [
        { opacity: 0, transform: 'scale(0.96)' },
        { opacity: 1, transform: 'none' },
      ];
  }
}

/** Plays an element's arrival. Fire and forget; input is never blocked. */
export function enter(
  element: Element | null | undefined,
  kind: Entrance,
  options: { duration?: number; delay?: number } = {},
): void {
  if (!canAnimate(element)) return;
  const reduce = reducedMotion();
  element.animate(frames(kind), {
    duration: reduce ? DURATION.fast : (options.duration ?? DURATION.normal),
    delay: reduce ? 0 : (options.delay ?? 0),
    easing: EASE.enter,
    fill: 'backwards',
  });
}

/**
 * Something that came from just below: it starts `distancePx` lower and travels
 * up into its place. Lab Notes plays it on the whole conversation when a
 * message is sent, so the new bubble comes out from behind the composer and
 * the older ones go up with it, as one movement. The request of 5 October 2026
 * (third), section 4.
 *
 * The arriving curve at the normal duration, and `transform` alone: the row is
 * already drawn in its final state, at full opacity, so nothing flickers. Does
 * nothing for less than a pixel or under reduced motion, where the caller fades
 * the new row in with `enter(row, 'fade')` instead.
 */
export function riseFrom(element: Element | null | undefined, distancePx: number): void {
  if (!canAnimate(element) || reducedMotion() || distancePx < 1) return;
  element.animate(
    [{ transform: `translateY(${String(distancePx)}px)` }, { transform: 'none' }],
    { duration: DURATION.normal, easing: EASE.enter },
  );
}

/**
 * A list on its first appearance: each of the first STAGGER_MAX items rises in
 * STAGGER_MS after the last; the rest arrive with the last staggered one.
 * Only visible items are passed in, and only once.
 */
export function stagger(elements: Iterable<Element>): void {
  let index = 0;
  for (const element of elements) {
    enter(element, 'rise', {
      duration: DURATION.normal,
      delay: Math.min(index, STAGGER_MAX) * STAGGER_MS,
    });
    index += 1;
  }
}

/** Where each element is now, to animate from after the DOM has changed. */
export function measure(elements: Iterable<Element>): Map<Element, DOMRect> {
  const places = new Map<Element, DOMRect>();
  for (const element of elements) places.set(element, element.getBoundingClientRect());
  return places;
}

/**
 * FLIP: an element that moved (a list re-sorted, a status changed) slides from
 * where it was to where it is, instead of jumping. Pass what `measure` took
 * before the change; call after it.
 */
export function flip(before: Map<Element, DOMRect>): void {
  if (reducedMotion()) return;
  for (const [element, was] of before) {
    if (!canAnimate(element) || !element.isConnected) continue;
    const now = element.getBoundingClientRect();
    const dx = was.left - now.left;
    const dy = was.top - now.top;
    if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
    element.animate(
      [{ transform: `translate(${String(dx)}px, ${String(dy)}px)` }, { transform: 'none' }],
      { duration: DURATION.normal, easing: EASE.move },
    );
  }
}

/**
 * A gentle horizontal shake for a refusal: a field left empty, a wrong
 * password. Three small swings, well under three a second's flash limit.
 */
export function shake(element: Element | null | undefined): void {
  if (!canAnimate(element) || reducedMotion()) return;
  element.animate(
    [
      { transform: 'translateX(0)' },
      { transform: 'translateX(-6px)' },
      { transform: 'translateX(5px)' },
      { transform: 'translateX(-3px)' },
      { transform: 'translateX(0)' },
    ],
    { duration: DURATION.slow, easing: EASE.move },
  );
}

/**
 * Something new arrived: it rises in where it belongs, and a soft tint over it
 * fades out, so the eye finds it. The tint is an overlay's opacity, never a
 * colour change that would repaint the row.
 */
export function arrive(element: Element | null | undefined): void {
  if (!canAnimate(element)) return;
  enter(element, 'rise');
  const tint = document.createElement('span');
  tint.setAttribute('aria-hidden', 'true');
  tint.style.cssText =
    'position:absolute;inset:0;border-radius:inherit;background:var(--gold-tint);pointer-events:none;';
  if (getComputedStyle(element).position === 'static') element.style.position = 'relative';
  element.appendChild(tint);
  const fade = tint.animate([{ opacity: 0.9 }, { opacity: 0 }], {
    duration: reducedMotion() ? DURATION.normal : 1400,
    easing: EASE.exit,
    fill: 'forwards',
  });
  fade.onfinish = () => tint.remove();
}
